<template>
    <div class="white-box p-20 p-b-5 dashboard-graph listing-index-graph">
        <h3 v-if="title" class="box-title">{{ title }}</h3>
        <div v-if="subtitle" class="listing-index-graph__subtitle">{{ subtitle }}</div>

        <!-- The indicator read failed (reason "error"): not the same as a region without data -->
        <div v-if="isError" class="listing-index-graph__error" role="alert">
            {{ $t("dashboard:listing_index:error") }}
        </div>

        <!-- Region without a v1 series (e.g. level-3 house regions) or no indicator values -->
        <div v-else-if="isEmpty" class="listing-index-graph__empty" role="status">
            {{ $t("dashboard:listing_index:empty") }}
        </div>

        <div v-else>
            <table v-if="showGrowth" class="listing-index-graph__growth">
                <tr class="listing-index-graph__caption">
                    <td>{{ $t("dashboard:last_24_months") }}</td>
                    <td>{{ $t("dashboard:last_12_months") }}</td>
                    <td>{{ $t("dashboard:last_quarter") }}</td>
                </tr>
                <tr class="listing-index-graph__value" :title="growthTooltip">
                    <td>{{ formatGrowth(apiData.growth.prevTwoYears) }}</td>
                    <td>{{ formatGrowth(apiData.growth.prevYear) }}</td>
                    <td>
                        {{ formatGrowth(apiData.growth.prevUnit) }}<span
                            v-if="growthProvisional"
                            class="listing-index-graph__mark"
                            aria-hidden="true">*</span>
                    </td>
                </tr>
            </table>

            <apexchart
                :type="chartKind"
                height="260"
                :options="chartOptions"
                :series="chartSeries"
            />

            <div v-if="hasProvisional" class="listing-index-graph__note">
                <span :class="['listing-index-graph__swatch', `listing-index-graph__swatch--${chartKind}`]" />
                {{ $t("dashboard:listing_index:provisional") }}
                <span v-if="growthProvisional"> * {{ $t("dashboard:listing_index:growth_provisional") }}</span>
            </div>
            <div v-if="basisNote" class="listing-index-graph__note">{{ basisNote }}</div>
            <div class="listing-index-graph__source">
                {{ $t("widgets:data_source:source") }}: {{ sourceText }}
                <help-icon :slug="infoSlug" />
            </div>
        </div>
    </div>
</template>
<script>
import HelpIcon from "@/components/widgets/HelpIcon.vue";
import VueApexCharts from "vue-apexcharts";

/**
 * One graph of the advertisement (asking-price) series: the v1 listing-index keys of
 * apps/analytics/services/listing_graphs.py, or the v0 key shown as "previous method (to 2023)".
 * Provisional points (apiData.flags[i].provisional) are drawn dashed with hollow markers on lines
 * and hatched on bars. Colours come from the Arvio design-system tokens (tokens.css on the page); when
 * a token is missing the chart keeps apexcharts' own colours (apexcharts uses `colors` as given, so an
 * undefined entry would draw an invisible series).
 */
const TOKEN_NAMES = {
    series: "--av-chart-1",
    other: "--av-chart-other",
    line: "--av-color-line",
    muted: "--av-color-muted",
    ink: "--av-color-ink",
    surface: "--av-color-surface",
};

let warnedMissingTokens = false;

/** The token colours, or null (with one console warning) when any of them is missing. */
function readTokens() {
    const tokens = {};
    const style = typeof window !== "undefined" ? window.getComputedStyle(document.documentElement) : null;
    Object.keys(TOKEN_NAMES).forEach(key => {
        tokens[key] = style ? style.getPropertyValue(TOKEN_NAMES[key]).trim() : "";
    });
    const missing = Object.keys(TOKEN_NAMES).filter(key => !tokens[key]).map(key => TOKEN_NAMES[key]);
    if (missing.length === 0)
        return tokens;
    if (!warnedMissingTokens) {
        warnedMissingTokens = true;
        console.warn(`ListingIndexGraph: design-system tokens missing (${missing.join(", ")}); is tokens.css loaded? `
                     + "Using the chart library's default colours.");
    }
    return null;
}

export default {
    name: "ListingIndexGraph",
    components: {
        apexchart: VueApexCharts,
        HelpIcon,
    },
    props: {
        title: { type: String, default: undefined },
        subtitle: { type: String, default: undefined },
        apiData: { type: Object, required: true },
        seriesName: { type: String, default: "" },
        kind: {  // "line" or "bar"
            type: String,
            default: "line",
            validator: value => ["line", "bar"].includes(value),
        },
        displayUnit: { type: String, default: "" },
        decimalPlaces: { type: Number, default: undefined },
        previousMethod: { type: Boolean, default: false },  // the v0 series, drawn as a grey reference line
        showGrowth: { type: Boolean, default: true },
        infoSlug: { type: String, default: "analytics-vir-in-metodologija-amas" },
    },
    data() {
        return { tokens: readTokens() };
    },
    computed: {
        isError() {
            return this.apiData.reason === "error";
        },
        isEmpty() {
            return Boolean(this.apiData.empty) || !this.apiData.data || this.apiData.data.length === 0;
        },
        chartKind() {
            return this.kind;
        },
        flags() {
            const flags = this.apiData.flags || [];
            return this.apiData.data.map((_, i) => Boolean(flags[i] && flags[i].provisional));
        },
        hasProvisional() {
            return this.flags.some(Boolean);
        },
        growthProvisional() {
            return Boolean(this.apiData.growth && this.apiData.growth.provisional);
        },
        growthTooltip() {
            return this.growthProvisional ? this.$t("dashboard:listing_index:growth_provisional") : undefined;
        },
        growthUnit() {
            return this.apiData.growthUnit === "pp" ? this.$t("dashboard:pp") : "%";
        },
        sourceText() {
            const source = this.apiData.dataSource || {};
            return [source.source, source.date, source.dataSourceNormalization].filter(Boolean).join(", ");
        },
        basisNote() {
            const basis = this.apiData.basis;
            if (typeof basis !== "string" || basis === "entry_dwellings")
                return undefined;
            return this.$t(`dashboard:listing_index:basis:${basis}`);
        },
        seriesColour() {
            if (!this.tokens)
                return undefined;
            return this.previousMethod ? this.tokens.other : this.tokens.series;
        },
        finalData() {
            return this.apiData.data.map((value, i) => (this.flags[i] ? null : value));
        },
        provisionalData() {
            // Provisional points plus the last final point before them, so the dashed segment joins the line.
            const firstProvisional = this.flags.indexOf(true);
            return this.apiData.data.map((value, i) => {
                if (this.flags[i])
                    return value;
                if (this.kind === "line" && firstProvisional > 0 && i === firstProvisional - 1)
                    return value;
                return null;
            });
        },
        chartSeries() {
            const name = this.seriesName;
            const series = [{ name, type: this.kind, data: this.finalData }];
            if (this.hasProvisional)
                series.push({ name: `${name} (${this.$t("dashboard:listing_index:provisional_short")})`,
                              type: this.kind, data: this.provisionalData });
            if (this.apiData.band) {
                const bandName = this.$t("dashboard:listing_index:band");
                series.push({ name: `${bandName} ↓`, type: "line", data: this.apiData.band.lo });
                series.push({ name: `${bandName} ↑`, type: "line", data: this.apiData.band.hi });
            }
            return series;
        },
        chartOptions() {
            const tokens = this.tokens;
            const isBar = this.kind === "bar";
            const discrete = !tokens ? [] : this.flags
                .map((provisional, i) => (provisional ? {
                    seriesIndex: 1,
                    dataPointIndex: i,
                    fillColor: tokens.surface,
                    strokeColor: this.seriesColour,
                    size: 5,
                } : null))
                .filter(Boolean);
            const options = {
                chart: {
                    type: this.kind,
                    stacked: isBar,
                    zoom: { enabled: false },
                    toolbar: { show: false },
                    parentHeightOffset: 15,
                    fontFamily: "inherit",
                },
                stroke: {
                    width: this.chartSeries.map((s, i) => (isBar && i < 2 ? 0 : (i < 2 ? 2.5 : 1))),
                    dashArray: this.chartSeries.map((s, i) => (i === 0 ? 0 : (i === 1 ? 5 : 2))),
                    curve: "straight",
                },
                fill: {
                    type: this.chartSeries.map((s, i) => (isBar && i === 1 ? "pattern" : "solid")),
                    opacity: 1,
                    pattern: { style: "slantedLines", width: 6, height: 6, strokeWidth: 2 },
                },
                markers: {
                    size: 0,
                    discrete: isBar ? [] : discrete,
                },
                plotOptions: { bar: { columnWidth: "55%" } },
                dataLabels: { enabled: false },
                labels: this.apiData.labels,
                grid: { xaxis: { lines: { show: false } } },
                xaxis: { type: "category", tooltip: { enabled: false } },
                yaxis: { labels: { formatter: this.formatValue, minWidth: 1 } },
                legend: { show: false },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: {
                        formatter: (value, { dataPointIndex }) => {
                            if (value === null || value === undefined)
                                return undefined;
                            const text = this.formatValue(value);
                            return this.flags[dataPointIndex]
                                ? `${text} (${this.$t("dashboard:listing_index:provisional_short")})` : text;
                        },
                    },
                },
            };
            if (tokens) {
                // Only with every token resolved: a missing one keeps apexcharts' defaults.
                options.colors = this.chartSeries.map((s, i) => (i < 2 ? this.seriesColour : tokens.other));
                options.chart.foreColor = tokens.muted;
                options.grid.borderColor = tokens.line;
            }
            return options;
        },
    },
    methods: {
        formatValue(value) {
            if (value === null || value === undefined)
                return "";
            const number = this.decimalPlaces !== undefined
                ? Number(value).toLocaleString("sl-SI", {
                    minimumFractionDigits: this.decimalPlaces, maximumFractionDigits: this.decimalPlaces })
                : Number(value).toLocaleString("sl-SI");
            return this.displayUnit ? `${number} ${this.displayUnit}` : number;
        },
        formatGrowth(value) {
            if (value === null || value === undefined || value === "X")
                return "–";
            return `${value} ${this.growthUnit}`;
        },
    },
};
</script>
<style lang="scss">
.listing-index-graph {
    color: var(--av-color-ink);

    .listing-index-graph__subtitle,
    .listing-index-graph__caption td,
    .listing-index-graph__note,
    .listing-index-graph__source {
        color: var(--av-color-muted);
        font-size: var(--av-font-size-caption);
    }
    .listing-index-graph__growth td {
        padding: 0 var(--av-space-2);
        border-right: 1px solid var(--av-color-line);
        &:first-child { padding-left: 0; }
        &:last-child { border-right: 0; }
    }
    .listing-index-graph__value {
        font-weight: var(--av-font-weight-medium);
    }
    .listing-index-graph__mark {
        color: var(--av-color-muted);
        cursor: help;
    }
    .listing-index-graph__empty {
        margin: var(--av-space-4) 0;
        padding: var(--av-space-4);
        border: 1px dashed var(--av-color-line-strong);
        border-radius: var(--av-radius-control);
        background: var(--av-color-hover);
        color: var(--av-color-muted);
        text-align: center;
    }
    .listing-index-graph__error {
        margin: var(--av-space-4) 0;
        padding: var(--av-space-4);
        border: 1px solid var(--av-color-warning-line);
        border-radius: var(--av-radius-control);
        background: var(--av-color-warning-soft);
        color: var(--av-color-warning-text);
        text-align: center;
    }
    .listing-index-graph__note {
        display: flex;
        align-items: center;
        gap: var(--av-space-2);
        margin-top: var(--av-space-1);
    }
    .listing-index-graph__swatch {
        display: inline-block;
        width: var(--av-space-4);
        height: var(--av-space-2);
        border-top: 2px dashed var(--av-chart-1);
    }
    .listing-index-graph__swatch--bar {
        height: var(--av-space-3);
        border: 1px solid var(--av-chart-1);
        background: repeating-linear-gradient(45deg, var(--av-chart-1) 0 2px, transparent 2px 5px);
    }
    .listing-index-graph__source {
        padding: var(--av-space-1) 0;
    }
}
</style>
